import React, { useEffect, useState } from "react";
import { Button, Form, Spinner, InputGroup, Table } from "react-bootstrap";
import ModalConfirmar from "../../components/ModalConfirmar";
import ModalNuevoPagoExtra from "./ModalNuevoPagoExtra";
import * as pagosExtraApi from "../../api/pagosExtra";
import { formatoNumero } from "../../utils/moneda";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

/** Proyección de pagos extra (gratificación de Navidad/Julio, bonos, etc.): cada tarjeta es
 * un mes/año con el monto esperado a recibir y los gastos proyectados (monto + descripción)
 * contra ese monto, para saber cuánto sobrará. Opción habilitada individualmente por usuario
 * (no por perfil) — si esta pantalla se puede ver es porque el backend ya lo autorizó. */
export default function PagosExtraPage() {
  const [pagos, setPagos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [showNuevo, setShowNuevo] = useState(false);
  const [aEliminar, setAEliminar] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try { setPagos(await pagosExtraApi.listarPagosExtra()); }
    finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, []);

  const confirmarEliminar = async () => {
    await pagosExtraApi.eliminarPagoExtra(aEliminar.pagoExtraId);
    setAEliminar(null);
    cargar();
  };

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h4 className="mb-0">Pagos Extra</h4>
        <Button size="sm" variant="primary" onClick={() => setShowNuevo(true)}>
          <i className="bi bi-plus-lg me-1"></i>Nuevo pago extra
        </Button>
      </div>

      {cargando ? <Spinner animation="border" /> : (
        <>
          {!pagos.length && <p className="text-muted">Aún no tienes pagos extra proyectados.</p>}
          <div className="row g-3">
            {pagos.map((p) => (
              <div className="col-12 col-lg-6" key={p.pagoExtraId}>
                <TarjetaPagoExtra pago={p} onEliminar={() => setAEliminar(p)} onCambio={cargar} />
              </div>
            ))}
          </div>
        </>
      )}

      <ModalNuevoPagoExtra show={showNuevo} onHide={() => setShowNuevo(false)} onGuardado={() => { setShowNuevo(false); cargar(); }} />
      <ModalConfirmar
        show={!!aEliminar}
        titulo="Eliminar pago extra"
        mensaje={`¿Eliminar la proyección de ${aEliminar ? MESES[aEliminar.mes - 1] : ""} ${aEliminar?.anio}? Se borrarán también sus gastos proyectados.`}
        onConfirmar={confirmarEliminar}
        onCancelar={() => setAEliminar(null)}
      />
    </div>
  );
}

function TarjetaPagoExtra({ pago, onEliminar, onCambio }) {
  const [gastos, setGastos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [descripcion, setDescripcion] = useState("");
  const [monto, setMonto] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [editandoMonto, setEditandoMonto] = useState(false);
  const [montoEditado, setMontoEditado] = useState(pago.montoEsperado);

  const cargarGastos = async () => {
    setCargando(true);
    try { setGastos(await pagosExtraApi.listarGastosPagoExtra(pago.pagoExtraId)); }
    finally { setCargando(false); }
  };

  useEffect(() => { cargarGastos(); }, [pago.pagoExtraId]); // eslint-disable-line react-hooks/exhaustive-deps

  const agregarGasto = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await pagosExtraApi.crearGastoPagoExtra(pago.pagoExtraId, Number(monto), descripcion);
      setDescripcion(""); setMonto("");
      await cargarGastos();
      onCambio();
    } finally {
      setGuardando(false);
    }
  };

  const eliminarGasto = async (gastoId) => {
    await pagosExtraApi.eliminarGastoPagoExtra(gastoId);
    await cargarGastos();
    onCambio();
  };

  const guardarMonto = async () => {
    setEditandoMonto(false);
    if (Number(montoEditado) === pago.montoEsperado || !Number(montoEditado)) {
      setMontoEditado(pago.montoEsperado);
      return;
    }
    await pagosExtraApi.actualizarMontoPagoExtra(pago.pagoExtraId, Number(montoEditado));
    onCambio();
  };

  return (
    <div className="fs-card p-0 overflow-hidden h-100 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center p-2 px-3" style={{ background: "var(--fs-alerta-bg)" }}>
        <span className="fw-bold">{MESES[pago.mes - 1]} {pago.anio}</span>
        <div className="d-flex align-items-center gap-2">
          {editandoMonto ? (
            <InputGroup size="sm" style={{ maxWidth: 130 }}>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number" step="0.01" min="0.01" autoFocus
                value={montoEditado}
                onChange={(e) => setMontoEditado(e.target.value)}
                onBlur={guardarMonto}
                onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              />
            </InputGroup>
          ) : (
            <span className="fw-bold" role="button" title="Click para editar el monto esperado" onClick={() => setEditandoMonto(true)}>
              ${formatoNumero(pago.montoEsperado)} <i className="bi bi-pencil-square text-muted small"></i>
            </span>
          )}
          <Button size="sm" variant="outline-dark" onClick={onEliminar}>
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      </div>

      {cargando ? <div className="p-3"><Spinner size="sm" animation="border" /></div> : (
        <Table size="sm" hover className="table-fs mb-0 align-middle">
          <tbody>
            {gastos.map((g) => (
              <FilaGasto key={g.pagoExtraGastoId} gasto={g} onEliminar={() => eliminarGasto(g.pagoExtraGastoId)} onGuardado={async () => { await cargarGastos(); onCambio(); }} />
            ))}
            {!gastos.length && (
              <tr><td colSpan={3} className="text-center text-muted py-3">Sin gastos proyectados.</td></tr>
            )}
          </tbody>
        </Table>
      )}

      <Form onSubmit={agregarGasto} className="d-flex gap-2 p-2 border-top">
        <Form.Control size="sm" placeholder="Descripción" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required />
        <InputGroup size="sm" style={{ maxWidth: 130 }}>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control type="number" step="0.01" min="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} required />
        </InputGroup>
        <Button size="sm" type="submit" variant="outline-primary" disabled={guardando}>
          {guardando ? <Spinner size="sm" animation="border" /> : <i className="bi bi-plus-lg"></i>}
        </Button>
      </Form>

      <div className="d-flex justify-content-between px-3 py-2 border-top fw-bold" style={{ background: "var(--fs-bg)" }}>
        <span>Gastos: <span className="text-fs-gasto">${formatoNumero(pago.totalGastos)}</span></span>
        <span>Restante: <span className={pago.restante >= 0 ? "text-fs-ingreso" : "text-fs-gasto"}>${formatoNumero(pago.restante)}</span></span>
      </div>
    </div>
  );
}

/** Fila de un gasto proyectado con descripción y monto editables cada uno por separado
 * (click para editar, se guarda solo al salir del campo). */
function FilaGasto({ gasto, onEliminar, onGuardado }) {
  const [editandoDescripcion, setEditandoDescripcion] = useState(false);
  const [descripcion, setDescripcion] = useState(gasto.descripcion);
  const [editandoMonto, setEditandoMonto] = useState(false);
  const [monto, setMonto] = useState(gasto.monto);

  const guardar = async (nuevaDescripcion, nuevoMonto) => {
    if (nuevaDescripcion === gasto.descripcion && Number(nuevoMonto) === gasto.monto) return;
    if (!nuevaDescripcion.trim() || !Number(nuevoMonto)) {
      setDescripcion(gasto.descripcion);
      setMonto(gasto.monto);
      return;
    }
    await pagosExtraApi.actualizarGastoPagoExtra(gasto.pagoExtraGastoId, Number(nuevoMonto), nuevaDescripcion.trim());
    onGuardado();
  };

  const guardarDescripcion = () => { setEditandoDescripcion(false); guardar(descripcion, monto); };
  const guardarMonto = () => { setEditandoMonto(false); guardar(descripcion, monto); };

  return (
    <tr>
      <td>
        {editandoDescripcion ? (
          <Form.Control
            size="sm" autoFocus value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            onBlur={guardarDescripcion}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          />
        ) : (
          <span role="button" title="Click para editar" onClick={() => setEditandoDescripcion(true)}>{gasto.descripcion}</span>
        )}
      </td>
      <td className="text-end" style={{ width: 110 }}>
        {editandoMonto ? (
          <InputGroup size="sm">
            <InputGroup.Text>$</InputGroup.Text>
            <Form.Control
              type="number" step="0.01" min="0.01" autoFocus value={monto}
              onChange={(e) => setMonto(e.target.value)}
              onBlur={guardarMonto}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            />
          </InputGroup>
        ) : (
          <span role="button" title="Click para editar" onClick={() => setEditandoMonto(true)}>${formatoNumero(gasto.monto)}</span>
        )}
      </td>
      <td style={{ width: 36 }}>
        <Button size="sm" variant="link" className="text-danger p-0" onClick={onEliminar}>
          <i className="bi bi-x-lg"></i>
        </Button>
      </td>
    </tr>
  );
}
